import { Fragment, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredMenu } from '../useAnchoredMenu';
import { RailButton } from './RailButton';
import { MoreIcon } from './icons';

export interface MoreMenuItem {
  key: string;
  label: string;
  /** One line under the label, for anything whose consequence is not obvious. */
  sub?: string;
  /** Right-hand mono note, e.g. "New tab" or a theme's On/Off. */
  hint?: string;
  icon: ReactNode;
  enabled: boolean;
  /** Shown in place of `sub` when the item is off. */
  reason?: string;
  danger?: boolean;
  onSelect: () => void;
}

export interface MoreMenuGroup {
  label: string;
  items: MoreMenuItem[];
}

const MENU_WIDTH = 250;

/**
 * The ⋯ menu at the end of the rail: everything Henry does occasionally, kept
 * out of the rail so the rail stays the things he does daily.
 */
export default function MoreMenu({ groups }: { groups: MoreMenuGroup[] }) {
  const menu = useAnchoredMenu({ width: MENU_WIDTH });

  function select(item: MoreMenuItem) {
    if (!item.enabled) return;
    // Close and hand focus back before acting: some items open a modal or a
    // new tab, and either would fight the menu for focus.
    menu.close({ focusAnchor: true });
    item.onSelect();
  }

  return (
    <>
      <RailButton
        ref={menu.anchorRef}
        icon={<MoreIcon />}
        label="More"
        haspopup="menu"
        pressed={menu.open}
        expanded={menu.open}
        onClick={menu.toggle}
      />

      {menu.open &&
        menu.popupStyle &&
        createPortal(
          <div ref={menu.menuRef} role="menu" className="brief-menu" style={menu.popupStyle}>
            {groups.map((group, i) => (
              <Fragment key={group.label}>
                {i > 0 && <span className="brief-menu__sep" />}
                <div className="brief-menu__head">{group.label}</div>
                {group.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    role="menuitem"
                    aria-disabled={item.enabled ? undefined : true}
                    className={`brief-menu__item${item.enabled ? '' : ' brief-menu__item--disabled'}${
                      item.danger ? ' brief-menu__item--danger' : ''
                    }`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => select(item)}
                  >
                    <span className={item.danger ? 'text-error' : 'text-faint'} aria-hidden>
                      {item.icon}
                    </span>
                    <span className="brief-menu__body">
                      <span className={`brief-menu__name${item.danger ? ' text-error' : ''}`}>
                        {item.label}
                      </span>
                      {/* A reason is a sentence, so it takes the gloss line
                          rather than the hint: in the 9px mono hint it would
                          eat the width the label needs and wrap it. */}
                      {(item.enabled ? item.sub : item.reason ?? item.sub) && (
                        <span className="brief-menu__gloss">
                          {item.enabled ? item.sub : item.reason ?? item.sub}
                        </span>
                      )}
                    </span>
                    {item.enabled && item.hint && (
                      <span className="brief-menu__hint">{item.hint}</span>
                    )}
                  </button>
                ))}
              </Fragment>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
